import React, { useEffect, useMemo, useRef } from "react";
import { Box, Typography, keyframes } from "@mui/material";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";

/** Stagger between letter pops on the grid. */
export const WORD_REVEAL_LETTER_STAGGER_MS = 50;
/** Cell glow / letter spring duration. */
export const WORD_REVEAL_ILLUMINATE_MS = 480;
/** Ghost word lift before flight. */
export const WORD_REVEAL_LIFT_MS = 280;
/** Flight duration toward the word bank. */
export const WORD_REVEAL_FLIGHT_MS = 720;
/** Particle lifetime during flight. */
export const WORD_REVEAL_PARTICLE_MS = 520;
/** Bank bounce after impact. */
export const WORD_REVEAL_BANK_BOUNCE_MS = 380;

export const WORD_REVEAL_TOTAL_MS =
    WORD_REVEAL_ILLUMINATE_MS + WORD_REVEAL_LIFT_MS + WORD_REVEAL_FLIGHT_MS + 80;

const cellGlowPulse = keyframes`
    0% {
        opacity: 0;
        transform: scale(0.92);
        box-shadow: 0 0 0 rgba(127, 184, 90, 0);
    }
    18% {
        opacity: 1;
        transform: scale(1.04);
        box-shadow: 0 0 18px rgba(127, 184, 90, 0.85);
    }
    55% {
        opacity: 1;
        transform: scale(1);
        box-shadow: 0 0 12px rgba(255, 255, 255, 0.55);
    }
    100% {
        opacity: 0;
        transform: scale(1);
        box-shadow: 0 0 0 rgba(127, 184, 90, 0);
    }
`;

/** Spring-like letter pop — applied on real grid letter nodes via celebrateCells. */
export const wordRevealLetterPopKeyframes = keyframes`
    0% { transform: scale(1); }
    35% { transform: scale(1.28); }
    65% { transform: scale(0.94); }
    100% { transform: scale(1); }
`;

/** Scale/glow only — position lives on the outer flight wrapper. */
const ghostLift = keyframes`
    0% {
        opacity: 0;
        transform: translate3d(-50%, -50%, 0) scale(0.85);
        text-shadow: 0 0 0 rgba(255, 255, 255, 0);
    }
    40% {
        opacity: 1;
        transform: translate3d(-50%, -50%, 0) scale(1.12);
        text-shadow: 0 0 16px rgba(255, 255, 255, 0.95), 0 0 28px rgba(127, 184, 90, 0.9);
    }
    100% {
        opacity: 1;
        transform: translate3d(-50%, -50%, 0) scale(1.06);
        text-shadow: 0 0 12px rgba(255, 255, 255, 0.8), 0 0 22px rgba(46, 125, 50, 0.75);
    }
`;

const particleFade = keyframes`
    0% {
        opacity: 0.95;
        transform: translate3d(0, 0, 0) scale(1);
    }
    100% {
        opacity: 0;
        transform: translate3d(var(--dx), var(--dy), 0) scale(0.2);
    }
`;

/** Bank container bounce on lock-in. */
export const wordBankLockInKeyframes = keyframes`
    0% { transform: scale(1); }
    40% { transform: scale(1.025); }
    70% { transform: scale(0.99); }
    100% { transform: scale(1); }
`;

/**
 * Read viewport rects for puzzle cells marked with `data-puzzle-cell="row,col"`.
 * @param {{ row: number, col: number }[]} cells
 * @param {ParentNode} [root]
 */
export function measureWordCellRects(cells, root = document) {
    if (!Array.isArray(cells) || cells.length === 0) return [];
    return cells
        .map((c) => {
            const el = root.querySelector(`[data-puzzle-cell="${c.row},${c.col}"]`);
            if (!el) return null;
            const r = el.getBoundingClientRect();
            if (r.width < 1 || r.height < 1) return null;
            return {
                row: c.row,
                col: c.col,
                left: r.left,
                top: r.top,
                width: r.width,
                height: r.height,
                letter: typeof c.letter === "string" ? c.letter : undefined,
            };
        })
        .filter(Boolean);
}

/** Word bank / collected-words panel marked with `data-word-bank`. */
export function measureWordBankRect(root = document) {
    const el = root.querySelector("[data-word-bank]");
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return null;
    return { left: r.left, top: r.top, width: r.width, height: r.height };
}

function boundingCenter(rects) {
    if (!rects.length) return { x: 0, y: 0 };
    let minL = Infinity;
    let minT = Infinity;
    let maxR = -Infinity;
    let maxB = -Infinity;
    for (const r of rects) {
        minL = Math.min(minL, r.left);
        minT = Math.min(minT, r.top);
        maxR = Math.max(maxR, r.left + r.width);
        maxB = Math.max(maxB, r.top + r.height);
    }
    return { x: (minL + maxR) / 2, y: (minT + maxB) / 2 };
}

function buildTrailParticles(seed, count = 8) {
    const particles = [];
    let s = (Number(seed) || 1) >>> 0;
    const rand = () => {
        s = (s * 1664525 + 1013904223) >>> 0;
        return s / 0xffffffff;
    };
    for (let i = 0; i < count; i += 1) {
        const angle = rand() * Math.PI * 2;
        const dist = 8 + rand() * 22;
        particles.push({
            id: `wr-p-${i}`,
            dx: `${Math.cos(angle) * dist}px`,
            dy: `${Math.sin(angle) * dist}px`,
            size: 3 + rand() * 3,
            delay: rand() * 120,
            hue: 85 + rand() * 45,
        });
    }
    return particles;
}

/**
 * Non-blocking word-reveal celebration: cell illumination, ghost word flight, sparkle trail.
 * pointer-events: none everywhere — play continues uninterrupted.
 */
export default function WordRevealFx({
    fxId,
    word,
    cellRects = [],
    bankRect = null,
    onComplete,
    onBankImpact,
}) {
    const ghostRef = useRef(null);
    const trailRef = useRef(null);
    const displayWord = String(word || "").trim();
    const start = useMemo(() => boundingCenter(cellRects), [cellRects]);
    const particles = useMemo(
        () => buildTrailParticles(fxId, 8),
        [fxId],
    );

    useEffect(() => {
        if (!displayWord || cellRects.length === 0) {
            onComplete?.(fxId);
            return undefined;
        }

        const ghost = ghostRef.current;
        const trail = trailRef.current;
        if (!ghost) {
            onComplete?.(fxId);
            return undefined;
        }

        const endX = bankRect
            ? bankRect.left + Math.min(bankRect.width * 0.28, 120)
            : start.x;
        const endY = bankRect
            ? bankRect.top + Math.min(bankRect.height * 0.18, 56)
            : start.y - 72;

        let cancelled = false;
        let flightAnim = null;
        let trailAnim = null;
        let impactTimer = null;
        let doneTimer = null;

        const liftTimer = window.setTimeout(() => {
            if (cancelled || !ghostRef.current) return;

            flightAnim = ghostRef.current.animate(
                [
                    {
                        offset: 0,
                        transform: `translate3d(${start.x}px, ${start.y}px, 0) scale(1)`,
                        opacity: 1,
                    },
                    {
                        offset: 0.7,
                        transform: `translate3d(${(start.x + endX) / 2}px, ${(start.y + endY) / 2 - 28}px, 0) scale(1.12)`,
                        opacity: 1,
                    },
                    {
                        offset: 1,
                        transform: `translate3d(${endX}px, ${endY}px, 0) scale(0.55)`,
                        opacity: 0.15,
                    },
                ],
                {
                    duration: WORD_REVEAL_FLIGHT_MS,
                    easing: "cubic-bezier(0.22, 1, 0.36, 1)",
                    fill: "forwards",
                },
            );

            if (trail) {
                trailAnim = trail.animate(
                    [
                        {
                            offset: 0,
                            transform: `translate3d(${start.x}px, ${start.y}px, 0)`,
                            opacity: 1,
                        },
                        {
                            offset: 1,
                            transform: `translate3d(${endX}px, ${endY}px, 0)`,
                            opacity: 0.4,
                        },
                    ],
                    {
                        duration: WORD_REVEAL_FLIGHT_MS,
                        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
                        fill: "forwards",
                    },
                );
            }

            impactTimer = window.setTimeout(() => {
                if (!cancelled) onBankImpact?.(fxId);
            }, Math.max(0, WORD_REVEAL_FLIGHT_MS - 40));
        }, WORD_REVEAL_ILLUMINATE_MS);

        doneTimer = window.setTimeout(() => {
            if (!cancelled) onComplete?.(fxId);
        }, WORD_REVEAL_TOTAL_MS);

        return () => {
            cancelled = true;
            window.clearTimeout(liftTimer);
            if (impactTimer) window.clearTimeout(impactTimer);
            if (doneTimer) window.clearTimeout(doneTimer);
            flightAnim?.cancel();
            trailAnim?.cancel();
        };
    }, [fxId, displayWord, cellRects, bankRect, start.x, start.y, onComplete, onBankImpact]);

    if (!displayWord || cellRects.length === 0) return null;

    const node = (
        <Box
            aria-hidden
            sx={{
                position: "fixed",
                inset: 0,
                zIndex: 1400,
                pointerEvents: "none",
                overflow: "visible",
            }}
        >
            {cellRects.map((r, i) => (
                <Box
                    key={`glow-${fxId}-${r.row}-${r.col}`}
                    sx={{
                        position: "fixed",
                        left: r.left,
                        top: r.top,
                        width: r.width,
                        height: r.height,
                        borderRadius: 0.5,
                        background:
                            "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(127,184,90,0.9) 45%, rgba(46,125,50,0.85) 100%)",
                        willChange: "transform, opacity",
                        animation: `${cellGlowPulse} ${WORD_REVEAL_ILLUMINATE_MS}ms ease-out forwards`,
                        animationDelay: `${i * WORD_REVEAL_LETTER_STAGGER_MS}ms`,
                        opacity: 0,
                    }}
                />
            ))}

            <Box
                ref={trailRef}
                sx={{
                    position: "fixed",
                    left: 0,
                    top: 0,
                    width: 0,
                    height: 0,
                    willChange: "transform",
                    transform: `translate3d(${start.x}px, ${start.y}px, 0)`,
                    opacity: 0,
                }}
            >
                {particles.map((p) => (
                    <Box
                        key={`${fxId}-${p.id}`}
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            width: p.size,
                            height: p.size,
                            marginLeft: -p.size / 2,
                            marginTop: -p.size / 2,
                            borderRadius: "50%",
                            background: `hsl(${p.hue} 75% 58%)`,
                            boxShadow: `0 0 6px hsl(${p.hue} 85% 62% / 0.9)`,
                            "--dx": p.dx,
                            "--dy": p.dy,
                            animation: `${particleFade} ${WORD_REVEAL_PARTICLE_MS}ms ease-out infinite`,
                            animationDelay: `${WORD_REVEAL_ILLUMINATE_MS + p.delay}ms`,
                            opacity: 0,
                        }}
                    />
                ))}
            </Box>

            <Box
                ref={ghostRef}
                sx={{
                    position: "fixed",
                    left: 0,
                    top: 0,
                    willChange: "transform, opacity",
                    transform: `translate3d(${start.x}px, ${start.y}px, 0)`,
                    pointerEvents: "none",
                }}
            >
                <Typography
                    component="div"
                    sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 999,
                        fontWeight: 800,
                        fontSize: { xs: "1.15rem", sm: "1.45rem" },
                        letterSpacing: 1.2,
                        color: "#fff",
                        whiteSpace: "nowrap",
                        pointerEvents: "none",
                        willChange: "transform, opacity",
                        background:
                            "linear-gradient(135deg, rgba(46,125,50,0.92), rgba(127,184,90,0.95))",
                        boxShadow: "0 10px 28px rgba(46, 125, 50, 0.4)",
                        transform: "translate3d(-50%, -50%, 0)",
                        animation: `${ghostLift} ${WORD_REVEAL_LIFT_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`,
                        animationDelay: `${Math.min(
                            cellRects.length * WORD_REVEAL_LETTER_STAGGER_MS,
                            200,
                        )}ms`,
                        opacity: 0,
                    }}
                >
                    {displayWord}
                </Typography>
            </Box>
        </Box>
    );

    if (typeof document === "undefined") return null;
    return createPortal(node, document.body);
}

WordRevealFx.propTypes = {
    fxId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    word: PropTypes.string.isRequired,
    cellRects: PropTypes.arrayOf(
        PropTypes.shape({
            row: PropTypes.number,
            col: PropTypes.number,
            left: PropTypes.number,
            top: PropTypes.number,
            width: PropTypes.number,
            height: PropTypes.number,
        }),
    ),
    bankRect: PropTypes.shape({
        left: PropTypes.number,
        top: PropTypes.number,
        width: PropTypes.number,
        height: PropTypes.number,
    }),
    onComplete: PropTypes.func,
    onBankImpact: PropTypes.func,
};
